import React, { useState, useCallback, useRef, useEffect } from 'react';
import { eventBus, webRTCService } from '../core';

type IdleState = 'active' | 'warning' | 'paused';

interface VideoPlayerProps {
  isActive: boolean;
  isBlurred?: boolean;
  inputEnabled: boolean;  // 简化为单一布尔值
  cursorStyle?: string;
  gameMode?: boolean;     // 游戏鼠标模式：禁用点击特效
  onVideoReady?: (video: HTMLVideoElement) => void;
  // Canvas zoom/pan
  zoom?: number;
  panX?: number;
  panY?: number;
  isPanMode?: boolean;
  onPanChange?: (panX: number, panY: number) => void;
}

interface Ripple {
  id: number;
  x: number;
  y: number;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  isActive,
  isBlurred = false,
  inputEnabled,
  cursorStyle = 'default',
  gameMode = false,
  onVideoReady,
  zoom = 1,
  panX = 0,
  panY = 0,
  isPanMode = false,
  onPanChange,
}) => {
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const [isInputActive, setIsInputActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [idleState, setIdleState] = useState<IdleState>('active');
  const [idleCountdown, setIdleCountdown] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Notify parent when video is ready
  useEffect(() => {
    if (videoRef.current && onVideoReady) {
      onVideoReady(videoRef.current);
    }
  }, [onVideoReady]);

  // Idle state listener
  useEffect(() => {
    const handler = (data: { state: IdleState; countdown?: number }) => {
      setIdleState(data.state);
      setIdleCountdown(data.countdown ?? 0);
    };
    eventBus.on('idle:stateChange', handler);
    return () => { eventBus.off('idle:stateChange', handler); };
  }, []);

  const handleIdleResume = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    webRTCService.resumeFromIdle();
  }, []);

  // Handle pointer down for ripple effect
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // 游戏模式下禁用点击特效
    if (!isActive || !inputEnabled || gameMode) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const id = Date.now();
    setRipples(prev => [...prev, { id, x, y }]);

    setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== id));
    }, 600);
  }, [isActive, inputEnabled, gameMode]);

  // Refs for pan values — avoid re-registering listeners on every pan update
  const panRef = useRef({ panX, panY });
  const onPanChangeRef = useRef(onPanChange);
  const isPanModeRef = useRef(isPanMode);
  useEffect(() => { panRef.current = { panX, panY }; }, [panX, panY]);
  useEffect(() => { onPanChangeRef.current = onPanChange; }, [onPanChange]);
  useEffect(() => { isPanModeRef.current = isPanMode; }, [isPanMode]);

  // Pan drag: isPanMode 或 Alt+拖拽，zoom > 1 时生效
  useEffect(() => {
    if (zoom <= 1) return;
    const container = containerRef.current;
    if (!container) return;

    let dragging = false;
    const start = { x: 0, y: 0, panX: 0, panY: 0 };

    const onDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      // 需要 isPanMode 或 Alt 键
      if (!isPanModeRef.current && !e.altKey) return;
      // 忽略 UI 元素上的点击
      if ((e.target as Element)?.closest?.('[data-ui-element]')) return;
      e.preventDefault();
      e.stopPropagation();
      dragging = true;
      start.x = e.clientX;
      start.y = e.clientY;
      start.panX = panRef.current.panX;
      start.panY = panRef.current.panY;
      setIsDragging(true);
    };
    const onMove = (e: MouseEvent) => {
      if (!dragging) return;
      onPanChangeRef.current?.(start.panX + e.clientX - start.x, start.panY + e.clientY - start.y);
    };
    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      setIsDragging(false);
    };

    // 在 window 上用 capture 阶段拦截，优先于 InputService
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('mousemove', onMove, true);
    window.addEventListener('mouseup', onUp, true);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('mousemove', onMove, true);
      window.removeEventListener('mouseup', onUp, true);
    };
  }, [zoom]);

  // Handle click to activate input
  const handleClick = useCallback(() => {
    if (isPanMode) return;
    if (isActive && !isInputActive) {
      setIsInputActive(true);
    }
  }, [isActive, isInputActive, isPanMode]);

  // Alt 键按下时显示 grab 光标（zoom > 1）
  const [isAltHeld, setIsAltHeld] = useState(false);
  useEffect(() => {
    const down = (e: KeyboardEvent) => { if (e.key === 'Alt') setIsAltHeld(true); };
    const up = (e: KeyboardEvent) => { if (e.key === 'Alt') setIsAltHeld(false); };
    const blur = () => setIsAltHeld(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); window.removeEventListener('blur', blur); };
  }, []);

  // Determine cursor style
  const canAltPan = isAltHeld && zoom > 1;
  const finalCursor = isPanMode || canAltPan
    ? (isDragging ? 'grabbing' : 'grab')
    : !isActive || !inputEnabled
      ? 'default'
      : (gameMode || isInputActive) ? cursorStyle : 'pointer';

  // Transform for zoom/pan
  const transformStyle = zoom !== 1
    ? `scale(${zoom}) translate(${panX / zoom}px, ${panY / zoom}px)`
    : undefined;

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 z-0 overflow-hidden bg-[#0a0a0c] flex items-center justify-center transition-all duration-500 ${isBlurred ? 'blur-sm scale-[1.02]' : ''}`}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      style={{ cursor: finalCursor }}
    >
      {/* Video element - always rendered, hidden when not active */}
      <video
        ref={videoRef}
        className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-500 ${isActive ? 'opacity-100' : 'opacity-0'}`}
        autoPlay
        playsInline
        muted
        style={{ pointerEvents: 'none', transform: transformStyle, transformOrigin: 'center center' }}
      />

      {/* Disconnected state - grid pattern background */}
      <div className={`absolute inset-0 transition-opacity duration-500 ${isActive ? 'opacity-0 pointer-events-none' : 'opacity-30'}`}>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        />

        {/* Ambient gradient orbs */}
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-blue-600/10 blur-[150px] rounded-full" />
        <div className="absolute -bottom-[20%] -right-[10%] w-[50%] h-[50%] bg-purple-600/5 blur-[150px] rounded-full" />
      </div>

      {/* Active state overlays */}
      {isActive && (
        <>
          {/* Click ripple effects */}
          {ripples.map(r => (
            <div
              key={r.id}
              className="absolute pointer-events-none border-2 border-blue-400/50 rounded-full animate-ping"
              style={{
                left: r.x - 20,
                top: r.y - 20,
                width: 40,
                height: 40,
                animationDuration: '0.6s',
              }}
            />
          ))}
        </>
      )}

      {/* Idle warning / paused overlay */}
      {isActive && idleState !== 'active' && (
        <div
          className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-opacity duration-300"
          data-ui-element
        >
          <div className="bg-[#202020] border border-white/10 rounded-lg shadow-2xl overflow-hidden w-[320px]">
            <div className="bg-[#2d2d2d] px-4 py-2.5 border-b border-white/5">
              <span className="text-sm font-medium text-slate-200">
                {idleState === 'warning' ? '\u7A7A\u95F2\u68C0\u6D4B' : '\u5DF2\u6682\u505C'}
              </span>
            </div>
            <div className="p-6 flex flex-col items-center">
              {idleState === 'warning' ? (
                <>
                  <div className="text-4xl font-light text-blue-400 mb-3">{idleCountdown}</div>
                  <div className="text-sm text-slate-400 mb-1">{'\u5373\u5C06\u6682\u505C\u89C6\u9891\u4F20\u8F93'}</div>
                  <div className="text-xs text-slate-500">{'\u70B9\u51FB\u9F20\u6807\u6216\u6309\u4E0B\u952E\u76D8\u53D6\u6D88'}</div>
                </>
              ) : (
                <>
                  <div className="text-sm text-slate-400 mb-4">{'\u89C6\u9891\u4F20\u8F93\u5DF2\u6682\u505C\uFF0C\u70B9\u51FB\u6062\u590D'}</div>
                  <button
                    onClick={handleIdleResume}
                    className="px-5 py-2 bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 rounded transition-colors cursor-pointer text-sm"
                  >
                    {'\u6062\u590D\u8FDE\u63A5'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(VideoPlayer);
